import { useEffect, useMemo, useState } from "react";
import { libList, libCreate, libUpdate, libDelete, libUpload } from "../../lib/admin";
import { Plus, Trash2, Pencil, Loader2, X, Upload, Tag, Palette as PaletteIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const absUrl = (rel) => (rel?.startsWith("http") ? rel : rel ? `${BACKEND_URL}${rel}` : null);

const LIBRARIES = [
    { key: "outfit", label: "Outfit", description: "Curated bride + groom outfits per event." },
    { key: "palette", label: "Color Palette", description: "Hand-tuned palettes the AI should pull from." },
    { key: "backdrop", label: "Backdrop", description: "Venues + lighting moods for the shot planner." },
    { key: "pose", label: "Pose", description: "Photographer-vetted pose references." },
    { key: "story_theme", label: "Story Theme", description: "Concept arcs the Story Studio can lean on." },
    { key: "decor", label: "Decor", description: "Decor + prop references coordinated to palettes." },
];

const EMPTY_FORM = {
    name: "",
    image_url: "",
    description: "",
    tags: [],
    palette: [],
    extras: {},
};

export default function AdminLibrary() {
    const [activeLib, setActiveLib] = useState("outfit");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | {} (creating) | item (editing)

    async function reload(lib = activeLib) {
        setLoading(true);
        try {
            const d = await libList(lib);
            setItems(d.items);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load library.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { reload(activeLib); /* eslint-disable-next-line */ }, [activeLib]);

    const meta = useMemo(() => LIBRARIES.find((l) => l.key === activeLib), [activeLib]);

    return (
        <div className="space-y-7" data-testid="admin-library">
            <div>
                <h1 className="font-serif text-3xl text-charcoal">Wedding Intelligence Library</h1>
                <p className="text-[13.5px] text-ink">
                    Curated reference data the AI report generator pulls from instead of hallucinating.
                </p>
            </div>

            {/* Sub-library nav */}
            <div className="flex flex-wrap gap-2" data-testid="library-tabs">
                {LIBRARIES.map((l) => (
                    <button
                        key={l.key}
                        type="button"
                        onClick={() => setActiveLib(l.key)}
                        data-testid={`lib-tab-${l.key}`}
                        className={`rounded-full px-4 py-2 text-[12.5px] transition ${
                            activeLib === l.key
                                ? "bg-charcoal text-ivory"
                                : "bg-white text-charcoal/70 ring-1 ring-charcoal/10 hover:text-charcoal"
                        }`}
                    >
                        {l.label}
                    </button>
                ))}
            </div>

            {/* Header strip */}
            <div className="flex flex-col gap-3 rounded-[20px] bg-white p-5 shadow-soft ring-1 ring-charcoal/5 md:flex-row md:items-center md:justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{meta.label} library</div>
                    <p className="text-[13px] text-ink">{meta.description}</p>
                </div>
                <button
                    type="button"
                    onClick={() => setEditing({ ...EMPTY_FORM })}
                    data-testid="lib-add-item"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-charcoal px-4 py-2.5 text-[12.5px] text-ivory transition hover:bg-charcoal/90"
                >
                    <Plus className="h-4 w-4" /> Add to {meta.label}
                </button>
            </div>

            {/* Grid */}
            {loading ? (
                <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-gold" /></div>
            ) : items.length === 0 ? (
                <div className="rounded-[20px] bg-white p-10 text-center shadow-soft ring-1 ring-charcoal/5">
                    <PaletteIcon className="mx-auto h-6 w-6 text-charcoal/30" />
                    <p className="mt-3 text-[13.5px] text-charcoal/55">Nothing here yet. Add your first {meta.label.toLowerCase()} entry.</p>
                </div>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="library-grid">
                    {items.map((it) => (
                        <ItemCard
                            key={it.id}
                            item={it}
                            onEdit={() => setEditing(it)}
                            onDelete={async () => {
                                if (!window.confirm(`Delete "${it.name}"?`)) return;
                                try {
                                    await libDelete(activeLib, it.id);
                                    toast.success("Deleted.");
                                    reload();
                                } catch (e) {
                                    toast.error(e?.response?.data?.detail || "Delete failed.");
                                }
                            }}
                        />
                    ))}
                </div>
            )}

            {editing !== null && (
                <ItemEditor
                    lib={activeLib}
                    item={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reload(); }}
                />
            )}
        </div>
    );
}

function ItemCard({ item, onEdit, onDelete }) {
    return (
        <div className="overflow-hidden rounded-[20px] bg-white shadow-soft ring-1 ring-charcoal/5" data-testid={`lib-item-${item.id}`}>
            {item.image_url ? (
                <img src={absUrl(item.image_url)} alt={item.name} className="aspect-[4/3] w-full object-cover" />
            ) : (
                <div className="flex aspect-[4/3] w-full items-center justify-center bg-champagne/40 text-charcoal/35">No image</div>
            )}
            <div className="p-4">
                <div className="font-serif text-lg text-charcoal">{item.name}</div>
                {item.description && <p className="mt-1 line-clamp-2 text-[12.5px] text-ink">{item.description}</p>}
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {(item.tags || []).slice(0, 5).map((t) => (
                        <span key={t} className="inline-flex items-center gap-1 rounded-full bg-champagne/60 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] text-charcoal">
                            <Tag className="h-2.5 w-2.5" /> {t}
                        </span>
                    ))}
                </div>
                {(item.palette || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {item.palette.slice(0, 8).map((h) => (
                            <span key={h} className="flex items-center gap-1.5 rounded-full bg-ivory px-2 py-0.5 text-[10px] ring-1 ring-charcoal/10">
                                <span className="h-2.5 w-2.5 rounded-full" style={{ background: h }} />
                                <span className="font-mono">{h}</span>
                            </span>
                        ))}
                    </div>
                )}
                <div className="mt-4 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onEdit}
                        data-testid={`lib-edit-${item.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-charcoal/15 px-3 py-1.5 text-[11px] text-charcoal hover:border-charcoal"
                    >
                        <Pencil className="h-3 w-3" /> Edit
                    </button>
                    <button
                        type="button"
                        onClick={onDelete}
                        data-testid={`lib-delete-${item.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 px-3 py-1.5 text-[11px] text-rose-700 hover:bg-rose-50"
                    >
                        <Trash2 className="h-3 w-3" /> Delete
                    </button>
                </div>
            </div>
        </div>
    );
}

function ItemEditor({ lib, item, onClose, onSaved }) {
    const [form, setForm] = useState(() => ({
        name: item.name || "",
        image_url: item.image_url || "",
        description: item.description || "",
        tags: item.tags || [],
        palette: item.palette || [],
    }));
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(item.id);

    function set(patch) { setForm((f) => ({ ...f, ...patch })); }

    async function handleUpload(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const { url } = await libUpload(lib, file);
            set({ image_url: url });
            toast.success("Image uploaded.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed.");
        } finally {
            setUploading(false);
        }
    }

    async function save() {
        if (!form.name.trim()) {
            toast.error("Name is required.");
            return;
        }
        setSaving(true);
        try {
            const body = {
                ...form,
                tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags,
                palette: typeof form.palette === "string" ? form.palette.split(",").map((s) => s.trim()).filter(Boolean) : form.palette,
            };
            if (isEdit) await libUpdate(lib, item.id, body);
            else await libCreate(lib, body);
            toast.success(isEdit ? "Updated." : "Created.");
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/55 backdrop-blur-sm" data-testid="lib-editor-modal">
            <div className="min-h-full w-full px-4 py-10">
                <div className="mx-auto max-w-2xl rounded-[24px] bg-ivory p-6 shadow-soft md:p-8">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{isEdit ? "Edit" : "Add"} · {lib}</div>
                            <h2 className="mt-1 font-serif text-2xl text-charcoal">{isEdit ? item.name : `New ${lib} item`}</h2>
                        </div>
                        <button onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-charcoal/10 text-charcoal/60 hover:border-charcoal hover:text-charcoal" data-testid="lib-editor-close">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    <div className="mt-6 space-y-4">
                        <Field label="Name">
                            <input value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="lib-field-name"
                                className="w-full rounded-full border border-charcoal/15 bg-white px-4 py-2.5 text-[13.5px] focus:border-charcoal focus:outline-none" />
                        </Field>

                        <Field label="Image">
                            <div className="flex items-center gap-3">
                                {form.image_url ? (
                                    <img src={absUrl(form.image_url)} alt="" className="h-20 w-20 rounded-lg object-cover ring-1 ring-charcoal/5" />
                                ) : (
                                    <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-champagne/40 text-charcoal/30 ring-1 ring-charcoal/5">—</div>
                                )}
                                <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-charcoal/15 px-3.5 py-2 text-[12px] text-charcoal hover:border-charcoal" data-testid="lib-upload-label">
                                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                                    {uploading ? "Uploading…" : (form.image_url ? "Replace image" : "Upload image")}
                                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="lib-upload-input" />
                                </label>
                            </div>
                        </Field>

                        <Field label="Description">
                            <textarea
                                value={form.description}
                                onChange={(e) => set({ description: e.target.value })}
                                rows={3}
                                data-testid="lib-field-description"
                                className="w-full rounded-2xl border border-charcoal/15 bg-white p-3 text-[13.5px] focus:border-charcoal focus:outline-none"
                            />
                        </Field>

                        <Field label="Tags (comma-separated)" hint='e.g. "luxury, warm, indoor"'>
                            <input
                                value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags}
                                onChange={(e) => set({ tags: e.target.value })}
                                data-testid="lib-field-tags"
                                className="w-full rounded-full border border-charcoal/15 bg-white px-4 py-2.5 text-[13.5px] focus:border-charcoal focus:outline-none"
                            />
                        </Field>

                        <Field label="Palette HEXes (comma-separated)" hint='e.g. "#FAF0DE, #D6B98C, #B9A287"'>
                            <input
                                value={Array.isArray(form.palette) ? form.palette.join(", ") : form.palette}
                                onChange={(e) => set({ palette: e.target.value })}
                                data-testid="lib-field-palette"
                                className="w-full rounded-full border border-charcoal/15 bg-white px-4 py-2.5 font-mono text-[12.5px] focus:border-charcoal focus:outline-none"
                            />
                        </Field>
                    </div>

                    <div className="mt-7 flex items-center justify-end gap-2">
                        <button onClick={onClose} disabled={saving} className="rounded-full border border-charcoal/15 px-4 py-2 text-[12px] text-charcoal hover:border-charcoal" data-testid="lib-editor-cancel">
                            Cancel
                        </button>
                        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2 text-[12px] font-medium text-charcoal hover:bg-goldDark disabled:opacity-60" data-testid="lib-editor-save">
                            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                            {saving ? "Saving…" : (isEdit ? "Save changes" : "Add to library")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/55">{label}</div>
            {hint && <div className="mt-0.5 text-[11px] text-charcoal/40">{hint}</div>}
            <div className="mt-2">{children}</div>
        </label>
    );
}
